import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { THEMES, assetUrl } from "@/lib/constants";
import { artworkAlt, artworkPath } from "@/lib/site";
import { Bindi, Reveal, SectionHeading } from "@/components/motifs";
import { ArtworkViewer } from "@/components/ArtworkViewer";

const ArtworkCard = ({ art, index, onOpen }) => (
  <motion.figure
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
    className="group mb-12 break-inside-avoid"
    data-testid={`gallery-artwork-card-${index}`}
  >
    {/* A real link so crawlers reach each catalogue page; a click opens the viewer. */}
    <Link
      to={artworkPath(art)}
      onClick={(e) => {
        e.preventDefault();
        onOpen();
      }}
      className="block w-full text-left"
      aria-label={`Explore ${art.title}`}
    >
      <div className="relative overflow-hidden border border-foreground/15 bg-paper p-3 transition-colors duration-500 group-hover:border-foreground/40">
        <div className="pointer-events-none absolute inset-1.5 border border-foreground/0 transition-colors duration-500 group-hover:border-accent/40" />
        <img
          src={assetUrl(art.image)}
          alt={artworkAlt(art)}
          width={art.width}
          height={art.height}
          loading="lazy"
          className="block h-auto w-full transition-transform duration-700 ease-out group-hover:scale-[1.015]"
        />
      </div>
      <figcaption className="mt-4 flex items-start justify-between gap-4 px-1">
        <div>
          <h3 className="font-serif text-xl font-medium tracking-tight transition-colors duration-300 group-hover:text-accent">
            {art.title}
          </h3>
          <p className="eyebrow mt-1.5 text-[10px] text-muted-foreground">
            {(art.themes || []).join(" · ")}
          </p>
        </div>
        <span className="mt-1 flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground transition-colors duration-300 group-hover:text-accent">
          Explore
          <ArrowUpRight size={13} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
        </span>
      </figcaption>
    </Link>
  </motion.figure>
);

export const GalleryChapter = ({ artworks = [], headingAs }) => {
  const [filter, setFilter] = useState("All");
  const [viewerIndex, setViewerIndex] = useState(null);

  const available = useMemo(() => {
    const present = new Set(artworks.flatMap((a) => a.themes || []));
    return ["All", ...THEMES.filter((t) => present.has(t))];
  }, [artworks]);

  const filtered = useMemo(
    () => (filter === "All" ? artworks : artworks.filter((a) => (a.themes || []).includes(filter))),
    [artworks, filter]
  );

  return (
    <section id="gallery" className="paper-surface border-y border-border" data-testid="gallery-section">
      <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
        <SectionHeading number="02" title="The Gallery" note="Original works · Undistorted" as={headingAs} />

        <Reveal className="mb-12 flex flex-wrap items-center gap-3">
          {available.map((t) => (
            <button
              key={t}
              onClick={() => setFilter(t)}
              data-testid={t === "All" ? "gallery-filter-all" : `gallery-filter-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={`flex items-center gap-2 rounded-full border px-4 py-2 font-mono text-[11px] uppercase tracking-[0.16em] transition-all duration-300 ${
                filter === t
                  ? "border-accent bg-accent text-accent-foreground"
                  : "border-border text-muted-foreground hover:border-foreground/40 hover:text-foreground"
              }`}
            >
              {filter === t && <Bindi size={6} className="bg-accent-foreground" />}
              {t}
            </button>
          ))}
        </Reveal>

        {filtered.length === 0 ? (
          <div className="flex flex-col items-center gap-4 py-20 text-center">
            <Bindi size={10} pulse />
            <p className="font-serif text-2xl italic text-muted-foreground">The walls are being hung — the collection is arriving.</p>
          </div>
        ) : (
          <div className="columns-1 gap-10 sm:columns-2 xl:columns-3">
            {filtered.map((art, i) => (
              <ArtworkCard key={art.id} art={art} index={i} onOpen={() => setViewerIndex(i)} />
            ))}
          </div>
        )}
      </div>

      <AnimatePresence>
        {viewerIndex !== null && filtered[viewerIndex] && (
          <ArtworkViewer
            artworks={filtered}
            index={viewerIndex}
            onClose={() => setViewerIndex(null)}
            onIndexChange={setViewerIndex}
          />
        )}
      </AnimatePresence>
    </section>
  );
};
